import type { Vehicle } from '@data/fms/vehicle/types';
import { MAX_ALERT_NUM, useNotification } from '@data/notification';
import type { MediaClient } from '@tier4/webauto-media-client';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useMount, usePrevious, useUnmount } from 'react-use';
import { DateTime } from 'luxon';
import { vehicleAlertListAtom } from '@data/notification/vehicle';
import { useSetAtom } from 'jotai';
import { VehicleAlertContent } from '@components/common/molecules';
import type { VehicleAlert } from '@data/notification/vehicle/types';

type DriveCallNotifierProps = {
  vehicle: Vehicle;
  telephone: MediaClient.RemoteTelephone;
};

const DriveCallNotifier = ({ vehicle, telephone }: DriveCallNotifierProps) => {
  const { notifyAlert } = useNotification();
  const [telephoneState, setTelephoneState] =
    useState<MediaClient.RemoteTelephone.State>();
  const prevTelephoneState = usePrevious(telephoneState);
  const setVehicleAlertList = useSetAtom(vehicleAlertListAtom);

  const notify = useCallback(
    (type: 'receiveCall' | 'hangupCall') => {
      const time = DateTime.now();
      const id = `${time.toFormat('yyyyMMddHHmmss')}_${type}_${
        vehicle.vehicle_id
      }`;
      const alertOption: VehicleAlert = {
        type,
        notifiedAt: time.toFormat('yyyy/MM/dd HH:mm:ss'),
        vehicle,
        driveOptions: {
          deviceTag: telephone.tag,
        },
      };
      notifyAlert({
        variant: type,
        alert: alertOption,
        message: (
          <div data-alert="true">
            <VehicleAlertContent alert={alertOption} />
          </div>
        ),
        enterFunction: () => {
          setVehicleAlertList((prevState) => {
            const clone = prevState.concat();
            if (clone.length === MAX_ALERT_NUM) {
              clone.pop();
            }
            return [{ ...alertOption, id }, ...clone];
          });
        },
      });
    },
    [notifyAlert, setVehicleAlertList, vehicle, telephone.tag],
  );

  const notifyReceive = useCallback(() => {
    // 電話がかかってきたときの通知
    notify('receiveCall');
  }, [notify]);

  const notifyHangup = useCallback(() => {
    // 電話が切れたときの通知
    notify('hangupCall');
  }, [notify]);

  const handleChangeState = useCallback(() => {
    setTelephoneState(telephone.state);
  }, [telephone]);

  const isMountedRef = useRef(false);
  const isConnectedRef = useRef(false);

  const disconnect = useCallback(async () => {
    if (!isConnectedRef.current) return;
    isConnectedRef.current = false;
    telephone.removeStateListener(handleChangeState);
    // offline のとき「だけ」disconnect をスキップする。
    // ライブラリの disconnect() は state === 'offline' のときのみ例外を投げるため。
    // ringing / online / calling / talking / error など offline 以外の状態は
    // disconnect が有効なので、ここでは return せずそのまま切断する。
    // (走行環境変更時などにデバイスが unregistered 状態のまま disconnect が
    //  呼ばれると InvalidStateError が発生するのを防ぐ)
    if (telephone.state === 'offline') return;
    try {
      await telephone.disconnect();
    } catch (error) {
      console.warn('failed to disconnect telephone', error);
    }
  }, [telephone, handleChangeState]);

  useMount(async () => {
    isMountedRef.current = true;
    try {
      // 既に接続済みのデバイスに対して connect を呼ぶと例外になるため状態を確認する
      if (telephone.state === 'offline') {
        await telephone.connect();
      }
      isConnectedRef.current = true;
      telephone.addStateListener(handleChangeState);
      // connect 完了前にアンマウントされていた場合はここで切断する
      if (!isMountedRef.current) {
        await disconnect();
      }
    } catch (error) {
      console.warn('failed to connect telephone', error);
    }
  });

  useUnmount(() => {
    isMountedRef.current = false;
    void disconnect();
  });

  useEffect(() => {
    if (prevTelephoneState !== 'ringing' && telephoneState === 'ringing') {
      // 電話がかかってきたときの状態変化を検知して通知
      notifyReceive();
    } else if (
      prevTelephoneState === 'ringing' &&
      telephoneState === 'online'
    ) {
      // 電話が切れたときの状態変化を検知して通知
      notifyHangup();
    }
  }, [telephoneState, prevTelephoneState, notifyReceive, notifyHangup]);

  return <></>;
};

export default memo(DriveCallNotifier);
